/**
 * Button — Business app primary button primitive.
 *
 * UX Invariants enforced (UX Blueprint §9):
 *   - `size="md"` default → 56px min height (UX invariant 1.1)
 *   - `size="lg"` / `intent="primary"` → 64px min height (critical actions)
 *   - `size="sm"` → 40px (chips/tags only, never for actions)
 *   - Every variant has color+icon (or text), never color-only
 *   - Respects `prefers-reduced-motion`
 *
 * This is a "pure prop" primitive — NO business logic, NO network calls.
 * It lives in `shared/ui/` to stay Lovable-regeneratable.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-biz-content',
    'font-medium rounded-biz-btn',
    'transition-colors duration-biz-fast ease-biz-default',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:pointer-events-none',
    'select-none touch-manipulation',
  ],
  {
    variants: {
      intent: {
        primary:
          'bg-biz-primary text-biz-primary-foreground hover:bg-biz-primary-hover active:bg-biz-primary-active',
        secondary:
          'bg-biz-surface text-biz-foreground border border-biz-border hover:bg-biz-muted',
        accent:
          'bg-biz-accent text-biz-accent-foreground hover:bg-biz-accent-hover',
        danger:
          'bg-biz-danger text-biz-danger-foreground hover:opacity-90',
        ghost:
          'bg-transparent text-biz-foreground hover:bg-biz-muted',
        link:
          'bg-transparent text-biz-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'min-h-biz-touch-sm px-biz-content text-biz-sm',
        md: 'min-h-biz-touch px-biz-content-lg text-biz-base',
        lg: 'min-h-biz-touch-primary px-biz-card text-biz-lg',
        keypad: 'min-h-biz-touch-keypad min-w-biz-touch-keypad px-biz-content-lg text-biz-xl',
        icon: 'min-h-biz-touch min-w-biz-touch p-0',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      intent: 'primary',
      size: 'md',
      fullWidth: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      intent,
      size,
      fullWidth,
      asChild = false,
      leftIcon,
      rightIcon,
      loading = false,
      disabled,
      children,
      ...rest
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ intent, size, fullWidth }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading ? (
          <span
            className="inline-block h-4 w-4 animate-spin rounded-biz-full border-2 border-current border-r-transparent"
            aria-hidden="true"
          />
        ) : leftIcon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {leftIcon}
          </span>
        ) : null}
        {children}
        {rightIcon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {rightIcon}
          </span>
        ) : null}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
